import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import "./Header.css";
import logo from "../Header/images/IOS.png";
import {
  FaShieldAlt,
  FaFileContract,
  FaChevronDown,
  FaBars,
  FaTimes,
  FaUserCircle,
  FaSignOutAlt,
  FaGraduationCap,
  FaBookOpen,
  FaCrown,
  FaEnvelope,
} from "react-icons/fa";

import { useAuth } from "../Authentication/AuthContext";
import Login from "../Authentication/Login/Login";
import SubscriptionMonthly from "../Authentication/Subscription/SubscriptionMonthly";

type WebsiteTheme = "light" | "dark";

type ThemeChangeDetail = {
  theme: WebsiteTheme;
};

type AuthChangeDetail = {
  isLoggedIn: boolean;
  username?: string;
};

type NavigationItem = {
  path: string;
  label: string;
  icon: React.ReactNode;
  matchPaths?: string[];
};

type HeaderAccessResponse = {
  has_full_access?: boolean;
  has_full_course_access?: boolean;
  has_full_book_access?: boolean;
  revenuecat_active_entitlements?: string[];
  revenuecat_active_products?: string[];
};

type HeaderAccessCache = {
  email: string;
  hasWebsitePremium: boolean;
  savedAt: number;
};

const WEBSITE_THEME_KEY = "dear_language_website_theme";
const SHARED_AUTH_KEY = "dear_language_demo_course_auth";
const SHARED_AUTH_USER_KEY = "dear_language_demo_user_name";
const SHARED_AUTH_EVENT = "dear-language-auth-change";
const COURSE_RESET_EVENT = "dear-language-course-reset";

const HEADER_ACCESS_CACHE_KEY = "dear_language_header_access_cache_v1";
const HEADER_ACCESS_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

const APP_TITLE = "DEUTSCH A1-C2";
const CREATOR_NAME = "Kugathaj Kangeswaran";

const PAGE_CLASSES_TO_CLEAR = [
  "main-screen-page",
  "course-screen-page",
  "courses-screen-page",
  "books-screen-page",
  "book-view-screen-page",
  "contact-screen-page",
  "privacy-screen-page",
  "terms-screen-page",
  "content-screen-page",
  "profile-screen-page",
];

const API_BASE_URL = (
  process.env.REACT_APP_BACKEND_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://www.api.dearlanguage.com"
).replace(/\/+$/, "");

const WEBSITE_ENTITLEMENT =
  process.env.REACT_APP_REVENUECAT_FULL_WEBSITE_ENTITLEMENT_ID ||
  "website_full_access";

const WEBSITE_PRODUCT =
  process.env.REACT_APP_REVENUECAT_FULL_WEBSITE_PACKAGE_ID ||
  "website_full_month";

const normalizeTheme = (value: string | null | undefined): WebsiteTheme => {
  return value === "dark" ? "dark" : "light";
};

const normalizeEmail = (email?: string | null): string => {
  return String(email || "")
    .trim()
    .toLowerCase();
};

const normalizeId = (value?: string | null): string => {
  return String(value || "").trim();
};

const normalizePathname = (pathname: string): string => {
  const cleanPathname = String(pathname || "/")
    .trim()
    .toLowerCase();

  if (!cleanPathname || cleanPathname === "/") {
    return "/";
  }

  return cleanPathname.replace(/\/+$/, "");
};

const normalizeList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => String(item || "").trim())
    .filter(Boolean);
};

const hasValue = (values: string[], expected: string): boolean => {
  const cleanExpected = expected.trim().toLowerCase();

  return values.some((value) => {
    const cleanValue = value.trim().toLowerCase();

    return cleanValue === cleanExpected || cleanValue.includes(cleanExpected);
  });
};

const responseHasWebsitePremium = (
  data: HeaderAccessResponse | null,
): boolean => {
  if (!data) {
    return false;
  }

  const activeEntitlements = normalizeList(data.revenuecat_active_entitlements);
  const activeProducts = normalizeList(data.revenuecat_active_products);

  return Boolean(
    data.has_full_access ||
      data.has_full_course_access ||
      data.has_full_book_access ||
      hasValue(activeEntitlements, WEBSITE_ENTITLEMENT) ||
      hasValue(activeProducts, WEBSITE_PRODUCT),
  );
};

const getStoredTheme = (): WebsiteTheme => {
  try {
    return normalizeTheme(window.localStorage.getItem(WEBSITE_THEME_KEY));
  } catch {
    return "light";
  }
};

const getSharedAuthState = (): AuthChangeDetail => {
  try {
    const isLoggedIn = window.localStorage.getItem(SHARED_AUTH_KEY) === "true";
    const username =
      window.localStorage.getItem(SHARED_AUTH_USER_KEY) || "kugathaj";

    return {
      isLoggedIn,
      username,
    };
  } catch {
    return {
      isLoggedIn: false,
      username: "kugathaj",
    };
  }
};

const setSharedAuthState = (nextState: AuthChangeDetail): void => {
  try {
    if (nextState.isLoggedIn) {
      window.localStorage.setItem(SHARED_AUTH_KEY, "true");
      window.localStorage.setItem(
        SHARED_AUTH_USER_KEY,
        nextState.username || "kugathaj",
      );
    } else {
      window.localStorage.removeItem(SHARED_AUTH_KEY);
      window.localStorage.removeItem(SHARED_AUTH_USER_KEY);
    }
  } catch {
    // localStorage kann blockiert sein.
  }

  window.dispatchEvent(
    new CustomEvent<AuthChangeDetail>(SHARED_AUTH_EVENT, {
      detail: nextState,
    }),
  );
};

const applyThemeToDocument = (theme: WebsiteTheme): void => {
  document.documentElement.setAttribute("data-theme", theme);

  document.body.classList.remove("website-theme-light", "website-theme-dark");
  document.body.classList.add(`website-theme-${theme}`);

  try {
    window.localStorage.setItem(WEBSITE_THEME_KEY, theme);
  } catch {
    // localStorage kann blockiert sein.
  }

  window.dispatchEvent(
    new CustomEvent<ThemeChangeDetail>("dear-language-theme-change", {
      detail: { theme },
    }),
  );
};

const safeReadHeaderAccessCache = (
  email: string,
): HeaderAccessCache | null => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return null;
    }

    const raw = window.sessionStorage.getItem(HEADER_ACCESS_CACHE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as HeaderAccessCache;

    if (!parsed || parsed.email !== email) {
      return null;
    }

    if (
      Date.now() - Number(parsed.savedAt || 0) >
      HEADER_ACCESS_CACHE_MAX_AGE_MS
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const safeWriteHeaderAccessCache = (
  email: string,
  hasWebsitePremium: boolean,
): void => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }

    const payload: HeaderAccessCache = {
      email,
      hasWebsitePremium,
      savedAt: Date.now(),
    };

    window.sessionStorage.setItem(
      HEADER_ACCESS_CACHE_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // sessionStorage kann blockiert sein.
  }
};

const safeClearHeaderAccessCache = (): void => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }

    window.sessionStorage.removeItem(HEADER_ACCESS_CACHE_KEY);
  } catch {
    // sessionStorage kann blockiert sein.
  }
};

const clearRoutePageClasses = (): void => {
  document.documentElement.classList.remove(...PAGE_CLASSES_TO_CLEAR);
  document.body.classList.remove(...PAGE_CLASSES_TO_CLEAR);
};

const forceHideOldFixedPages = (): void => {
  const fixedSelectors = [
    ".books-page",
    ".course-home-container",
    ".course-page",
    ".content-page",
    ".book-view-page",
    ".contact-page",
    ".privacy-policy-page",
    ".terms-page",
    ".profile-page",
  ];

  fixedSelectors.forEach((selector) => {
    document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
      element.style.pointerEvents = "none";
      element.style.visibility = "hidden";
      element.style.opacity = "0";
    });
  });
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showSubscriptionMonthly, setShowSubscriptionMonthly] =
    useState<boolean>(false);
  const [hasWebsitePremium, setHasWebsitePremium] = useState<boolean>(false);
  const [sharedAuth, setSharedAuth] =
    useState<AuthChangeDetail>(getSharedAuthState);

  const userDropdownRef = useRef<HTMLDivElement | null>(null);

  const {
    user,
    userId,
    userEmail,
    isLoggedIn: authContextLoggedIn,
    logout,
  } = useAuth();

  const location = useLocation();

  const cleanPathname = normalizePathname(location.pathname);
  const cleanEmail = normalizeEmail(userEmail);
  const cleanUserId = normalizeId(userId);

  const firebaseName =
    user?.displayName?.trim() || cleanEmail.split("@")[0] || "";

  const isUserLoggedIn = sharedAuth.isLoggedIn || authContextLoggedIn;

  const loggedInName =
    firebaseName || sharedAuth.username || cleanEmail.split("@")[0] || "kugathaj";

  const topNavigationItems = useMemo<NavigationItem[]>(
    () => [
      {
        path: "/courses",
        label: "Kurs",
        icon: <FaGraduationCap />,
        matchPaths: ["/courses"],
      },
      {
        path: "/books",
        label: "Bücher",
        icon: <FaBookOpen />,
        matchPaths: ["/books", "/book-view"],
      },
    ],
    [],
  );

  const bottomNavigationItems = useMemo<NavigationItem[]>(
    () => [
      {
        path: "/contact",
        label: "Kontakt",
        icon: <FaEnvelope />,
        matchPaths: ["/contact"],
      },
      {
        path: "/privacy-policy",
        label: "Datenschutzerklärung",
        icon: <FaShieldAlt />,
      },
      {
        path: "/terms-of-service",
        label: "Nutzungsbedingungen",
        icon: <FaFileContract />,
      },
    ],
    [],
  );

  const isActiveRoute = useCallback(
    (item: NavigationItem): boolean => {
      const paths = item.matchPaths ?? [item.path];

      return paths.some((path) => {
        const cleanPath = normalizePathname(path);

        if (cleanPath === "/book-view") {
          return cleanPathname === "/book-view";
        }

        return cleanPathname === cleanPath;
      });
    },
    [cleanPathname],
  );

  const activeMenuLabel = useMemo(() => {
    return (
      [...topNavigationItems, ...bottomNavigationItems].find((item) =>
        isActiveRoute(item),
      )?.label ?? "Menü"
    );
  }, [bottomNavigationItems, isActiveRoute, topNavigationItems]);

  const closeHeaderOverlays = useCallback((): void => {
    setIsMenuOpen(false);
    setShowUserMenu(false);
    setShowLoginModal(false);
    setShowSubscriptionMonthly(false);
  }, []);

  const loadHeaderAccess = useCallback(
    async (forceRefresh = false): Promise<void> => {
      if (!isUserLoggedIn || !cleanEmail) {
        setHasWebsitePremium(false);
        return;
      }

      if (!forceRefresh) {
        const cached = safeReadHeaderAccessCache(cleanEmail);

        if (cached) {
          setHasWebsitePremium(cached.hasWebsitePremium);
          return;
        }
      }

      try {
        const query = new URLSearchParams({
          email: cleanEmail,
        });

        if (cleanUserId) {
          query.set("app_user_id", cleanUserId);
        }

        if (forceRefresh) {
          query.set("force_refresh", "1");
          query.set("header_refresh", String(Date.now()));
        }

        const response = await fetch(
          `${API_BASE_URL}/api/protected-books?${query.toString()}`,
          {
            method: "GET",
            cache: forceRefresh ? "no-store" : "default",
            headers: {
              Accept: "application/json",
            },
          },
        );

        if (!response.ok) {
          setHasWebsitePremium(false);
          return;
        }

        const data = (await response.json()) as HeaderAccessResponse;
        const nextHasWebsitePremium = responseHasWebsitePremium(data);

        setHasWebsitePremium(nextHasWebsitePremium);
        safeWriteHeaderAccessCache(cleanEmail, nextHasWebsitePremium);
      } catch {
        setHasWebsitePremium(false);
      }
    },
    [cleanEmail, cleanUserId, isUserLoggedIn],
  );

  const goToPath = useCallback(
    (path: string): void => {
      const nextPath = normalizePathname(path);

      closeHeaderOverlays();

      if (nextPath === "/courses") {
        window.dispatchEvent(new CustomEvent(COURSE_RESET_EVENT));
        window.dispatchEvent(new CustomEvent("dear-language-course-page-opened"));
      }

      if (nextPath === "/books") {
        window.dispatchEvent(new CustomEvent("dear-language-books-page-opened"));
      }

      if (nextPath === "/contact") {
        window.dispatchEvent(new CustomEvent("dear-language-contact-page-opened"));
      }

      if (nextPath === "/profile") {
        window.dispatchEvent(new CustomEvent("dear-language-profile-page-opened"));
      }

      window.dispatchEvent(
        new CustomEvent("dear-language-route-change", {
          detail: {
            pathname: nextPath,
            search: "",
          },
        }),
      );

      clearRoutePageClasses();
      forceHideOldFixedPages();

      if (window.location.pathname === nextPath) {
        window.location.reload();
        return;
      }

      window.location.assign(nextPath);
    },
    [closeHeaderOverlays],
  );

  useEffect(() => {
    const initialTheme = getStoredTheme();
    applyThemeToDocument(initialTheme);
  }, []);

  useEffect(() => {
    const currentState = getSharedAuthState();
    setSharedAuth(currentState);

    const handleAuthChange = (event: Event): void => {
      const customEvent = event as CustomEvent<AuthChangeDetail>;

      setSharedAuth({
        isLoggedIn: Boolean(customEvent.detail?.isLoggedIn),
        username: customEvent.detail?.username || "kugathaj",
      });

      setShowUserMenu(false);
      setShowLoginModal(false);
    };

    window.addEventListener(SHARED_AUTH_EVENT, handleAuthChange);

    return () => {
      window.removeEventListener(SHARED_AUTH_EVENT, handleAuthChange);
    };
  }, []);

  useEffect(() => {
    if (authContextLoggedIn && !sharedAuth.isLoggedIn) {
      setSharedAuthState({
        isLoggedIn: true,
        username: firebaseName || sharedAuth.username || "kugathaj",
      });
    }
  }, [
    authContextLoggedIn,
    firebaseName,
    sharedAuth.isLoggedIn,
    sharedAuth.username,
  ]);

  useEffect(() => {
    if (!isUserLoggedIn || !cleanEmail) {
      setHasWebsitePremium(false);
      return;
    }

    void loadHeaderAccess(false);
  }, [cleanEmail, isUserLoggedIn, loadHeaderAccess]);

  useEffect(() => {
    const handleProfileAccessLoaded = (event: Event): void => {
      const customEvent = event as CustomEvent<{
        hasWebsitePremium?: boolean;
      }>;

      if (typeof customEvent.detail?.hasWebsitePremium === "boolean") {
        setHasWebsitePremium(customEvent.detail.hasWebsitePremium);
        safeWriteHeaderAccessCache(
          cleanEmail,
          customEvent.detail.hasWebsitePremium,
        );
      }
    };

    const handleAccessRefresh = (): void => {
      safeClearHeaderAccessCache();
      void loadHeaderAccess(true);
    };

    window.addEventListener(
      "dear-language-profile-access-loaded",
      handleProfileAccessLoaded,
    );
    window.addEventListener("dear-language-profile-refresh", handleAccessRefresh);
    window.addEventListener(
      "dear-language-books-access-refresh",
      handleAccessRefresh,
    );
    window.addEventListener(
      "dear-language-course-access-refresh",
      handleAccessRefresh,
    );

    return () => {
      window.removeEventListener(
        "dear-language-profile-access-loaded",
        handleProfileAccessLoaded,
      );
      window.removeEventListener(
        "dear-language-profile-refresh",
        handleAccessRefresh,
      );
      window.removeEventListener(
        "dear-language-books-access-refresh",
        handleAccessRefresh,
      );
      window.removeEventListener(
        "dear-language-course-access-refresh",
        handleAccessRefresh,
      );
    };
  }, [cleanEmail, loadHeaderAccess]);

  useEffect(() => {
    setIsMenuOpen(false);
    setShowUserMenu(false);
    setShowLoginModal(false);
    setShowSubscriptionMonthly(false);
  }, [cleanPathname]);

  useEffect(() => {
    const closeDropdowns = (event: MouseEvent): void => {
      const target = event.target as Node | null;

      if (
        userDropdownRef.current &&
        target &&
        !userDropdownRef.current.contains(target)
      ) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", closeDropdowns);

    return () => {
      document.removeEventListener("mousedown", closeDropdowns);
    };
  }, []);

  const toggleMenu = useCallback((): void => {
    setIsMenuOpen((current) => !current);
    setShowUserMenu(false);
  }, []);

  const toggleUserMenu = useCallback((): void => {
    if (!isUserLoggedIn) {
      setShowLoginModal(true);
      setShowUserMenu(false);
      setIsMenuOpen(false);
      setShowSubscriptionMonthly(false);
      return;
    }

    setShowUserMenu((current) => !current);
    setIsMenuOpen(false);
    setShowLoginModal(false);
    setShowSubscriptionMonthly(false);
  }, [isUserLoggedIn]);

  const openSubscription = useCallback((): void => {
    if (!isUserLoggedIn) {
      setShowLoginModal(true);
      setShowUserMenu(false);
      setIsMenuOpen(false);
      setShowSubscriptionMonthly(false);
      return;
    }

    setShowSubscriptionMonthly(true);
    setShowUserMenu(false);
    setIsMenuOpen(false);
    setShowLoginModal(false);
  }, [isUserLoggedIn]);

  const handleSubscriptionDone = useCallback((): void => {
    setShowSubscriptionMonthly(false);
    setHasWebsitePremium(true);
    safeClearHeaderAccessCache();

    window.dispatchEvent(new CustomEvent("dear-language-books-access-refresh"));
    window.dispatchEvent(new CustomEvent("dear-language-course-access-refresh"));

    void loadHeaderAccess(true);
  }, [loadHeaderAccess]);

  const handleLogout = useCallback((): void => {
    logout();

    setSharedAuthState({
      isLoggedIn: false,
      username: "",
    });

    setSharedAuth({
      isLoggedIn: false,
      username: "kugathaj",
    });

    setHasWebsitePremium(false);
    safeClearHeaderAccessCache();
    closeHeaderOverlays();

    if (cleanPathname === "/profile") {
      window.location.assign("/books");
    }
  }, [cleanPathname, closeHeaderOverlays, logout]);

  return (
    <div className="top-header-shell">
      <Login isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />

      <SubscriptionMonthly
        isOpen={showSubscriptionMonthly}
        onClose={() => setShowSubscriptionMonthly(false)}
        onSubscribed={handleSubscriptionDone}
      />

      <header className="top-header">
        <div className="top-header-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>

        <div className="logo-menu-container">
          <div className="logo-container">
            <button
              type="button"
              className="logo-link"
              aria-label={APP_TITLE}
              onClick={() => goToPath("/")}
            >
              <img
                src={logo}
                alt="Deutsch A1-C2 Logo"
                className="logo"
                onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                  event.currentTarget.src =
                    "https://via.placeholder.com/80x80?text=A1-C2";
                }}
              />

              <span className="logo-text-stack">
                <span className="brand-title">{APP_TITLE}</span>
                <span className="brand-subtitle">{CREATOR_NAME}</span>
              </span>
            </button>
          </div>

          <nav
            className={`menu ${isMenuOpen ? "open" : ""}`}
            aria-label="Hauptmenü"
          >
            {topNavigationItems.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`menu-item ${isActiveRoute(item) ? "active" : ""}`}
                onClick={() => goToPath(item.path)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="header-actions">
            {!hasWebsitePremium && (
              <button
                type="button"
                className="header-subscription-button"
                onClick={openSubscription}
                aria-label="Premium öffnen"
              >
                <span className="header-subscription-icon">
                  <FaCrown />
                </span>

                <span className="header-subscription-text">
                  <span>Premium</span>
                  <small>4,99 €</small>
                </span>
              </button>
            )}

            <div className="header-user-menu" ref={userDropdownRef}>
              <button
                type="button"
                className={`header-user-button ${
                  isUserLoggedIn ? "header-user-logged" : "header-user-login"
                }`}
                onClick={toggleUserMenu}
                aria-haspopup={isUserLoggedIn ? "menu" : undefined}
                aria-expanded={isUserLoggedIn ? showUserMenu : undefined}
                aria-label={isUserLoggedIn ? "Benutzermenü öffnen" : "Anmelden"}
              >
                <span className="header-user-avatar">
                  <FaUserCircle />
                </span>

                <span className="header-user-text">
                  <span>{isUserLoggedIn ? loggedInName : "Anmelden"}</span>
                  <small>
                    {isUserLoggedIn
                      ? hasWebsitePremium
                        ? "Premium-Zugang"
                        : "Zugang"
                      : "Registrieren"}
                  </small>
                </span>

                {isUserLoggedIn && (
                  <FaChevronDown className="header-user-arrow" />
                )}
              </button>

              {isUserLoggedIn && showUserMenu && (
                <div className="header-user-dropdown" role="menu">
                  <button
                    type="button"
                    className="header-user-dropdown-top"
                    onClick={() => goToPath("/profile")}
                    role="menuitem"
                    aria-label="Profil öffnen"
                  >
                    <FaUserCircle />

                    <span>
                      <strong>{loggedInName}</strong>
                      <small>
                        {hasWebsitePremium
                          ? "Premium-Zugang aktiv"
                          : "Zugang aktiv"}
                      </small>
                    </span>
                  </button>

                  <button
                    type="button"
                    className="header-user-logout"
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    <FaSignOutAlt />
                    Abmelden
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              className="menu-toggle"
              onClick={toggleMenu}
              aria-label="Menü öffnen"
              aria-expanded={isMenuOpen}
            >
              <span className="selected-menu">{activeMenuLabel}</span>
              {isMenuOpen ? <FaTimes size={17} /> : <FaBars size={17} />}
            </button>
          </div>
        </div>
      </header>

      <div className="contact-info">
        <div className="bottom-header-left">
          <nav className="bottom-legal-menu" aria-label="Unteres Menü">
            {bottomNavigationItems.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`bottom-legal-link ${
                  isActiveRoute(item) ? "active" : ""
                }`}
                onClick={() => goToPath(item.path)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Header;